import { provideAppInitializer, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStore } from '@app/core/auth/stores/auth-store';
import { ProjectsService } from '@app/core/services/projects.service';
import { SessionInitService } from '@app/core/auth/services/session-init.service';
import { LoginResponse } from '@app/core/auth/models';
import { devLog, devError } from '@app/core/utils/dev-logger';

const ALLOWED_ORIGINS = [
  'https://staging.sentinelradar.ai',
  'https://intel.sentinelradar.ai',
  'http://localhost:5173',
  'http://localhost:5174',
  'http://localhost:5175',
];

interface EmbedAuthMessage {
  type: 'sentinel-radar:auth';
  payload: LoginResponse;
  activeProjectId?: number;
}

function isEmbedAuthMessage(data: unknown): data is EmbedAuthMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'sentinel-radar:auth'
  );
}

/**
 * Lets sentinel-radar hand off an authenticated session via postMessage
 * instead of showing our login screen. Origin-checked against
 * ALLOWED_ORIGINS — a forged token still fails signature checks on real
 * API calls, this only guards the client-side "authenticated" UI state.
 */
export function provideEmbedSessionBridge() {
  return provideAppInitializer(() => {
    const authStore = inject(AuthStore);
    const sessionInit = inject(SessionInitService);
    const projectsService = inject(ProjectsService);
    const router = inject(Router);

    let handoffInFlight = false;

    window.addEventListener('message', (event: MessageEvent) => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) return;
      if (!isEmbedAuthMessage(event.data)) return;
      if (handoffInFlight) return;
      handoffInFlight = true;

      const { payload, activeProjectId } = event.data;

      devLog(`📨 EmbedSessionBridge: session handoff received from ${event.origin}`);
      authStore.setAuth(payload);

      sessionInit
        .initializeSession(payload)
        .then(async (result) => {
          if (!result.success) {
            devError('❌ EmbedSessionBridge: session init failed', result.errors);
            return;
          }

          if (activeProjectId != null) {
            await projectsService.setActiveProject(activeProjectId, true);
          }

          const project = projectsService.activeProject();
          if (project) {
            await router.navigate(['/project', project.id, 'dashboard']);
          } else {
            await router.navigate(['/'], { onSameUrlNavigation: 'reload' });
          }

          sessionInit.completeInitialization();
        })
        .catch((err) => devError('❌ EmbedSessionBridge: session init threw', err))
        .finally(() => {
          handoffInFlight = false;
        });
    });
  });
}
